import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import type { PartSize } from './partsRun';

// The catalog, as the parts table reads it: enough to answer "is this part already in
// the database?" for a part on screen right now.
//
// The extraction answers that question too — the model is shown the catalog and returns
// the item each part IS — but it answers it ONCE, at the moment it runs. Save a part
// afterwards and its own row still said NON, because the run that said so happened
// before the row existed. The answer is not a fact about a part, it is a fact about a
// part AND a database, and only one of them is frozen in the extraction.
//
// So the model's match stands (it sees the drawing, and knows a barrel nut by any
// name), and this checks the rest by name, live, against the catalog as it is now.

export type CatalogEntry = {
  id: number; nameEn: string; nameFr: string; category: string;
  // THE ROW'S OWN SIZE, which the server has always sent and this type never named. It is
  // the one measurement of a part that outlives a re-run of the phase that read it: the
  // parts list is rebuilt from the manual every press, the row is not. See ThreeDStep, where
  // a mesh anchored to the parts list alone lost its scale the moment the list was re-read.
  size?: PartSize | null;
  // The manual's drawings of this part, ids only — their bytes come from schematicSrc.
  // Optional because this type is the READING of the catalog, and everything else here
  // reads names; a caller that wants pictures wants useItemSchematics below.
  // `docId`: the manual each was cut from.
  schematics?: { id: number; docId?: string | null }[];
};

let cache: CatalogEntry[] | null = null;
let inflight: Promise<CatalogEntry[]> | null = null;
// Bumped whenever the catalog changes under us. A read that was already in the air when
// that happened describes the catalog as it was BEFORE the change — so it answers its
// own caller and is not allowed to become the cache. Without this, a save landing
// during a read would be undone by the read.
let epoch = 0;

function load(): Promise<CatalogEntry[]> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    const mine = epoch;
    inflight = axios
      .get<{ items: CatalogEntry[] }>('/api/notice/catalog')
      .then(({ data }) => {
        const items = data.items || [];
        if (mine === epoch) cache = items;
        return items;
      })
      // A read that failed leaves the cache NULL, not empty. The difference is the
      // whole point of `loaded` below: an empty catalog is an answer ("nothing is
      // saved"), a failed read is not, and a caller told the catalog is empty would
      // conclude that nothing in it exists.
      .catch(() => [])
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

// Everyone reading the catalog right now. The cache is shared, so a change to it is a
// change to what they are all looking at — and dropping it quietly is not enough: they
// have already read it, and nothing would make them read it again.
const listeners = new Set<() => void>();

// The catalog changed: forget it, and tell whoever is reading. Called after a save or a
// delete, so a table stops offering to save a part that is now in the database — and
// stops claiming one that is not.
// The catalog outside a render — for a caller that needs the answer once, not a subscription.
export const readCatalogItems = async (): Promise<CatalogEntry[]> => {
  await load();
  return cache ?? [];
};

export function invalidateCatalogItems() {
  cache = null;
  // Anything in the air was asked before this change and cannot answer it.
  epoch += 1;
  inflight = null;
  listeners.forEach((notify) => notify());
}

// Subscribe to the catalog (fetched once, module-cached, refetched when it changes).
// Every parts table on the page shares the one fetch.
//
// `loaded` is not a spinner — it is whether this list is EVIDENCE. Callers use the
// catalog to decide that something is not in it, and "I have not looked yet" and "I
// looked and it is not there" are opposite answers to that. Before the read lands,
// while it is being read again, and after a read that failed, there is no answer here
// at all — which is exactly when a caller must keep believing whatever it already
// believed.
export function useCatalogItems(): { items: CatalogEntry[]; loaded: boolean } {
  const [items, setItems] = useState<CatalogEntry[] | null>(cache);
  useEffect(() => {
    let alive = true;
    const read = () => {
      void load().then(() => {
        if (alive) setItems(cache);
      });
    };
    const onChange = () => {
      // Back to knowing nothing until the new read lands. Not to the OLD list: it is
      // wrong now, and it is wrong in the one way that matters — it is missing the row
      // that was just saved, which is the row being asked about.
      if (alive) setItems(null);
      read();
    };
    listeners.add(onChange);
    read();
    return () => {
      alive = false;
      listeners.delete(onChange);
    };
  }, []);
  return { items: items ?? [], loaded: items !== null };
}

// One part's drawings, oldest first, out of the catalog everyone here already shares.
// Empty for a part with none, and for a run with no part behind it.
//
// The catalog is fetched once for the whole page (see the cache above), so asking for one
// item's pictures costs nothing beyond what the parts table already paid for.
export function useItemSchematics(itemId: number | null): { id: number; docId?: string | null }[] {
  const { items } = useCatalogItems();
  return useMemo(
    () => (itemId == null ? [] : items.find((i) => i.id === itemId)?.schematics ?? []),
    [items, itemId],
  );
}

// THIS MANUAL'S DRAWING OF A PART, PUT ON THE PART'S ROW — and the schematic it is there (the
// same crop twice is one row). What a build is handed when it is made from the drawing on screen
// rather than from one the row already held: see DOC_DRAWING in ThreeDStep.
export async function keepDrawing(itemId: number, docId: string, schematic: string): Promise<number> {
  const { data } = await axios.post<{ id: number }>(
    `/api/notice/catalog/items/${itemId}/schematic`, { docId, schematic },
  );
  invalidateCatalogItems();
  return data.id;
}

// Whether an id still names a row. Ids come from elsewhere — an extraction that ran
// last week, a save from this session — and elsewhere is not now: the row it named can
// have been deleted since.
export function catalogHas(items: CatalogEntry[], id: number | null): boolean {
  return id != null && items.some((i) => i.id === id);
}

// A name reduced to what it means, for telling one item from another.
//
// The twin of _name_key in notice_catalog.py, and it has to stay its twin: that one
// decides whether saving creates a row, this one decides whether we offer to. If they
// disagree, the button offers to save a part that the save will refuse to duplicate —
// it would work, and it would look like a lie. Two runtimes, one rule; there is no
// third place to put it.
export function nameKey(name: string): string {
  return (name || '')
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    // The combining marks NFKD just split off ("é" -> "e" + U+0301). Written as escapes
    // because a literal class of invisible characters is a class nobody can review.
    .replace(/[\u0300-\u036f]/g, '')
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .map((w) => w.replace(/s$/, ''))
    .join(' ');
}

const sameCategory = (a: string, b: string) =>
  (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

// The catalog item this part is, by name, or null.
//
// Per category, exactly as the server's guard is: "Base" under Chairs and "Base" under
// Lamps are two parts sharing a word. Blunt on purpose — it will never see that "Screw"
// and "Bolt" are one part. That is the model's job, and this is the part it cannot do:
// knowing what was saved five minutes ago.
export function findCatalogItem(
  items: CatalogEntry[],
  part: { nameEn?: string | null; nameFr?: string | null; category?: string | null },
): number | null {
  const keys = new Set([nameKey(part.nameEn || ''), nameKey(part.nameFr || '')]);
  keys.delete('');
  if (!keys.size) return null;
  const hit = items.find(
    (i) =>
      sameCategory(i.category, part.category || '') &&
      [nameKey(i.nameEn), nameKey(i.nameFr)].some((k) => k && keys.has(k)),
  );
  return hit ? hit.id : null;
}

// THE FRENCH NAMES A FRESH PARTS LIST GAVE THE PARTS IT FOUND IN THE DATABASE, sent back to it.
// The model copies a matched part's English name and writes its French one afresh (see the
// parts prompt), so this is how a better French name reaches every manual. Once per run, not
// per render: a manual read before would otherwise send its older name back over the new one.
//
// A catalog id held by two parts is skipped — the list tells their names apart with a mark
// (see nameApart), and the mark is not the part's name.
export async function setCatalogNamesFr(
  parts: { catalogId: number | null; nameFr: string | null }[],
): Promise<void> {
  const seen = new Map<number, number>();
  for (const p of parts) if (p.catalogId != null) seen.set(p.catalogId, (seen.get(p.catalogId) ?? 0) + 1);
  const names: Record<number, string> = {};
  for (const p of parts) {
    if (p.catalogId != null && seen.get(p.catalogId) === 1 && p.nameFr?.trim()) names[p.catalogId] = p.nameFr.trim();
  }
  if (!Object.keys(names).length) return;
  try {
    const { data } = await axios.post<{ renamed: number[] }>('/api/notice/catalog/names-fr', { names });
    if (data.renamed?.length) invalidateCatalogItems();
  } catch {
    /* the database keeps the name it had */
  }
}

// Give the database a size for the parts in it that have none.
//
// A part's size is read in a NOTICE; the row it belongs to is shared across every notice.
// The two facts meet only in a browser holding both, and the one moment a save could
// carry the size across happens once — a part already in the database shows as found
// rather than offering to be saved again. So this runs whenever a manual's parts are on
// screen, quietly, and the server fills only the rows that are still null.
//
// Fire and forget, and silent when it fails. Nobody asked for it: it is the page noticing
// that it knows something the database does not, and a failure to write it is not news to
// interrupt a phase with.
export async function fillCatalogSizes(
  parts: { catalogId: number | null; size: unknown }[],
): Promise<void> {
  const sizes: Record<number, unknown> = {};
  for (const p of parts) {
    if (p.catalogId != null && p.size) sizes[p.catalogId] = p.size;
  }
  if (!Object.keys(sizes).length) return;
  try {
    const { data } = await axios.post<{ filled: number[] }>(
      '/api/notice/catalog/sizes', { sizes },
    );
    // Only when something changed: the catalogue's cached copy now has a stale size, and
    // re-reading it on every parts render would be a request per notice for nothing.
    if (data.filled?.length) invalidateCatalogItems();
  } catch {
    /* the database keeps the size it had */
  }
}
